/**
 * WEB-2141 — ЦЕЛЬ ВОЗВРАТА кнопкой «назад» с чужой страницы, сторона
 * посетителя.
 *
 * Настройку задаёт автор воронки (`funnels.settings.returnTarget`), она
 * доезжает сюда блоком `funnelSettings` публичного ответа квиза (дорога
 * построена карточкой WEB-2154). Здесь — только ЧТЕНИЕ: зеркало ветки чтения
 * серверной двери `backend/src/funnels/utils/return-target.ts`.
 *
 * ПОЧЕМУ ЧИТАТЕЛЬ ПОВТОРЯЕТ ПРАВИЛО, А НЕ ВЕРИТ СЕРВЕРУ. Настройки едут
 * свободным jsonb: строка бывает старше поля, бывает записана прошлой версией
 * кода. Уводить посетителя по адресу, который сегодня уже не проходит дверь
 * безопасности, нельзя — `window.location.assign` исполнит и `javascript:`.
 * Поэтому адрес проверяется ПЕРЕД уходом, тем же правилом схем, что и все
 * прочие адреса плеера (`@/utils/isAllowedUrl`).
 */
import { isAllowedUrl } from '@/utils/isAllowedUrl';

export const RETURN_TARGET_KINDS = ['store', 'url', 'stay'] as const;
export type ReturnTargetKind = (typeof RETURN_TARGET_KINDS)[number];

export interface ReturnTarget {
  kind: ReturnTargetKind;
  url?: string;
}

/**
 * Умолчание — «приложение или магазин»: ровно то, что делала ловушка до этой
 * карточки. Воронка, заведённая раньше настройки, обязана вести себя как
 * прежде (шаг приёмки 19).
 */
export const DEFAULT_RETURN_TARGET: ReturnTarget = { kind: 'store' };

/** Блок настроек воронки из публичного ответа квиза (WEB-2154). */
export interface FunnelSettingsBlock {
  funnelId?: string;
  settings?: Record<string, unknown> | null;
}

function isKind(value: unknown): value is ReturnTargetKind {
  return (
    typeof value === 'string' &&
    (RETURN_TARGET_KINDS as readonly string[]).includes(value)
  );
}

function isUsableUrl(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= 1000 &&
    !value.includes('{{') &&
    !value.includes('}}') &&
    /^https?:\/\//i.test(value) &&
    isAllowedUrl(value)
  );
}

/**
 * Цель возврата этой воронки.
 *
 * Негодная форма → умолчание («как было»). Заказанный автором произвольный
 * адрес, который не проходит дверь, → `stay`, а не `store`: перехват автор
 * заказал, но цели нет, и увести его посетителя в магазин, которого он не
 * выбирал, значило бы подменить его решение. Событие возврата при этом
 * пишется в любом случае — метрика не теряется.
 */
export function readReturnTarget(
  funnelSettings: FunnelSettingsBlock | null | undefined,
): ReturnTarget {
  const stored = funnelSettings?.settings?.['returnTarget'];

  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) {
    return DEFAULT_RETURN_TARGET;
  }

  const raw = stored as { kind?: unknown; url?: unknown };

  if (!isKind(raw.kind)) {
    return DEFAULT_RETURN_TARGET;
  }

  if (raw.kind !== 'url') {
    return { kind: raw.kind };
  }

  const url = typeof raw.url === 'string' ? raw.url.trim() : '';
  return isUsableUrl(url) ? { kind: 'url', url } : { kind: 'stay' };
}
